import { Minus, Plus } from "@/components/ui/icons";
import { Pressable, Text, View } from "react-native";

import { PickButton, withReason } from "@/components/browse/PickButton";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/** Shared outer height so grid overlays remain anchored when picking becomes a stepper. */
export const PICK_CONTROL_SIZE = 52;

/**
 * The browse pick control (#341), handling multiple copies (#646).
 *
 * A vendor's `StockItem` with `qty=N` is one unit and one `public_id` for N identical copies, so
 * picking several is a quantity on one line, not several lines.
 *
 * It renders one of two shapes:
 * - With one copy available (`available <= 1`) or nothing picked yet, the plain `PickButton`
 *   (+ / ✓), which sets the quantity to 1 or back to 0.
 * - With several copies available and at least one picked, a `−  N  +` stepper capped at
 *   `available`, so a customer cannot pick more than the vendor has.
 *
 * `onChange` receives the desired absolute quantity (0 removes the line). The browse screen wires
 * it to `usePullSheet.setPickQuantity`, and the server serializes and clamps it.
 */
export function PickControl({
  quantity,
  available,
  onChange,
  disabled = false,
  overlay = false,
  lockedReason = null,
  productName,
}: {
  productName?: string;
  quantity: number;
  available: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  /** Opaque backing for sitting over card art (grid cell); plain otherwise (list row). */
  overlay?: boolean;
  /**
   * Why picking is unavailable right now; the whole control is disabled and speaks this as its
   * hint (TRO-673).
   *
   * Set once the customer's list is `presented`: it is in the Vendor's queue and the server refuses
   * to change it, so a live stepper could fork a second list and lose the customer's place in line.
   * `null` while picking is open.
   */
  lockedReason?: string | null;
}) {
  const locked = lockedReason != null;
  const unusable = disabled || locked;
  if (available <= 1 || quantity === 0) {
    return (
      <PickButton
        productName={productName}
        picked={quantity > 0}
        onToggle={() => onChange(quantity > 0 ? 0 : 1)}
        overlay={overlay}
        disabled={unusable}
        hint={lockedReason ?? undefined}
      />
    );
  }

  const atMax = quantity >= available;
  return (
    <View
      className={cn(
        "h-[52px] flex-row items-center rounded-full border-2 border-primary",
        overlay ? "bg-bg/90" : "bg-surface",
        unusable && "opacity-50",
      )}
    >
      <StepButton
        kind="minus"
        // At 1, stepping down removes the line, so the label says so.
        label={quantity <= 1
          ? (productName ? `Remove ${productName} from your list` : "Remove from your list")
          : (productName ? `Remove one copy of ${productName}` : "Remove one copy")}
        onPress={() => onChange(quantity - 1)}
        disabled={unusable}
        hint={lockedReason}
      />
      <Text
        className="min-w-[22px] text-center font-num-bold text-sm text-foreground"
        accessibilityLabel={`${quantity} of ${available} picked`}
      >
        {quantity}
      </Text>
      <StepButton
        kind="plus"
        label={productName ? `Add one copy of ${productName}` : "Add one copy"}
        onPress={() => onChange(quantity + 1)}
        disabled={unusable || atMax}
        hint={lockedReason}
      />
    </View>
  );
}

function StepButton({
  kind,
  label,
  onPress,
  disabled,
  hint = null,
}: {
  kind: "minus" | "plus";
  label: string;
  onPress: () => void;
  disabled: boolean;
  hint?: string | null;
}) {
  const c = useThemeColors();
  const Icon = kind === "minus" ? Minus : Plus;
  return (
    <Pressable
      testID={`browse-item-qty-${kind}`}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={withReason(label, hint)}
      accessibilityHint={hint ?? undefined}
      accessibilityState={{ disabled }}
      // The 52px outer control leaves 48px targets inside its border.
      className={cn("w-12 self-stretch items-center justify-center", disabled && "opacity-40")}
    >
      <Icon size={16} color={c.primary} strokeWidth={3} />
    </Pressable>
  );
}
